import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { stdnum } from "stdnum";
import type { IntlShape } from "react-intl";

import type { SupportedCountry } from "../components/interface";

import { checkLuhn } from "./luhnValidation";

dayjs.extend(customParseFormat);

const isValidIsoDate = (isoDate: string): boolean => dayjs(isoDate, "YYYY-MM-DD", true).isValid();

type IdValidator = (value?: string) => boolean;

const isValidUnitedStatesSSN: IdValidator = (value?: string): boolean => {
  // @link https://en.wikipedia.org/wiki/Social_Security_number
  return /^(?!(000|666|9))(\d{3}-?(?!(00))\d{2}-?(?!(0000))\d{4})$/.test(value ?? "");
};

const isValidBritishNIN: IdValidator = (value?: string): boolean => {
  // @link https://en.wikipedia.org/wiki/National_Insurance_number
  return /^(?!BG)(?!GB)(?!NK)(?!KN)(?!TN)(?!NT)(?!ZZ)(?:[A-CEGHJ-PR-TW-Z][A-CEGHJ-NPR-TW-Z])(?:\s*\d\s*){6}([A-D]|\s)$/i.test(
    value ?? ""
  );
};

const isValidSpanishNIF: IdValidator = (value?: string): boolean => {
  // @link https://es.wikipedia.org/wiki/N%C3%BAmero_de_identificaci%C3%B3n_fiscal#C%C3%B3digo_de_control_para_n%C3%BAmeros_de_DNI_o_NIE
  const validChars = "TRWAGMYFPDXBNJZSQVHLCKE";
  const nifRexp = /^[0-9]{8}[A-HJ-NP-TV-Z]$/i;

  if (!value || !nifRexp.test(value)) {
    return false;
  }

  const letter = value.slice(-1);
  const charIndex = parseInt(value.substring(0, 8)) % 23;

  if (validChars.charAt(charIndex) === letter) {
    return true;
  }

  return false;
};

const isValidIrishPPS: IdValidator = (value?: string): boolean => {
  // @link https://en.wikipedia.org/wiki/Personal_Public_Service_Number
  const validationRegex = /^(\d{7})([A-Z]{1,2})$/i;

  if (!value) {
    return false;
  }

  if (!validationRegex.test(value)) {
    return false;
  }

  const parts = validationRegex.exec(value);

  if (!parts) {
    return false;
  }

  const [, numericPart, checksumCharacter] = parts;

  let multiplyingFactor = 8;
  let sum = 0;

  for (let i = 0; i < numericPart.length; i++) {
    sum += (numericPart[i] as unknown as number) * multiplyingFactor--;
  }

  if (checksumCharacter[1]) {
    sum += (checksumCharacter[1].toUpperCase().charCodeAt(0) - 64) * 9;
  }

  // The check character maps a remainder of 0-22 onto this alphabet, where 0 -> "W".
  // A plain `checksum + 64` mapping is wrong for remainder 0 (it yields "@" instead of "W"),
  // which incorrectly rejected every otherwise-valid PPS number ending in "W".
  const checkAlphabet = "WABCDEFGHIJKLMNOPQRSTUV";
  const checksum = sum % 23;
  return checkAlphabet[checksum] === checksumCharacter[0].toUpperCase();
};

const isValidBelgianNationalNumber: IdValidator = (value?: string): boolean => {
  // @link https://fr.wikipedia.org/wiki/Num%C3%A9ro_de_registre_national#Composition

  if (!value) {
    return false;
  }

  const validationRegex = /^(?<year>\d{2})(?<month>\d{2})(?<day>\d{2})(?<counter>\d{3})(?<checksum>\d{2})$/;
  const regexMatch = validationRegex.exec(value);
  if (!regexMatch || !regexMatch.groups) {
    return false;
  }
  const { year, month, day, counter, checksum } = regexMatch.groups;
  const birthDateIsUnknown = year === "00" && month === "00" && day === "01";
  const birthDateIsIncomplete = year !== "00" && month === "00" && day === "00";

  const yearNumerical = yearNumericalFromCharactersCode(year);

  if (!birthDateIsUnknown && !birthDateIsIncomplete) {
    // Birthdate must be valid: (no 30th February, etc.)
    const isoDate = `${yearNumerical}-${month}-${day}`;
    if (!isValidIsoDate(isoDate)) {
      return false;
    }
  }

  // Checksum check:
  let checksumBase = `${year}${month}${day}${counter}`;
  if (yearNumerical >= 2000) {
    checksumBase = "2" + checksumBase;
  }
  const expectedChecksum = 97 - (parseInt(checksumBase) % 97);

  return expectedChecksum === parseInt(checksum);
};

const isValidDanishNationalNumber: IdValidator = (value?: string): boolean => {
  // @link https://en.wikipedia.org/wiki/Personal_identification_number_(Denmark)
  if (!value) {
    return false;
  }

  const validationRegex = /^(?<day>\d{2})(?<month>\d{2})(?<year>\d{2})(?<sequenceNumber>\d{4})$/;
  const regexMatch = validationRegex.exec(value);
  if (!regexMatch || !regexMatch.groups) {
    return false;
  }
  const { year, month, day } = regexMatch.groups;

  const yearNumerical = yearNumericalFromCharactersCode(year);
  const isoDate = `${yearNumerical}-${month}-${day}`;
  if (!isValidIsoDate(isoDate)) {
    return false;
  }

  // And that's all, folks!
  //   > The numbers used to validate using a checksum but since the sequence numbers
  //   > ran out this was abandoned in 2007. It is also not possible to use the
  //   > checksum only for numbers that have a birth date before that because the
  //   > numbers are also assigned to immigrants.
  // So, yeah... Not much more we can do there  ¯\_(ツ)_/¯
  return true;
};

const isValidSwedishNationalNumber: IdValidator = (value?: string): boolean => {
  // @link https://en.wikipedia.org/wiki/Personal_identity_number_(Sweden)
  if (!value) {
    return false;
  }

  const validationRegex = /^(?<year>\d{2,4})(?<month>\d{2})(?<day>\d{2})(?<serialNumber>\d{3})(?<checksum>\d)$/;
  const regexMatch = validationRegex.exec(value);
  if (!regexMatch || !regexMatch.groups) {
    return false;
  }
  const { year, month, day } = regexMatch.groups;

  const yearNumerical = yearNumericalFromCharactersCode(year);
  const isoDate = `${yearNumerical}-${month}-${day}`;
  if (!isValidIsoDate(isoDate)) {
    return false;
  }

  const checksumScope = value.length === 10 ? value : value.substring(2);

  return checkLuhn(checksumScope);
};

const yearNumericalFromCharactersCode = (year: string): number => {
  let yearNumerical = parseInt(year);

  if (year.length === 4) {
    return yearNumerical;
  }

  const currentYear = new Date().getFullYear() - 2_000;
  if (yearNumerical > currentYear) {
    // N.B. We could assume than anyone born less than 16-18 years ago would also fall in that category, but then
    // they would fall somewhere around the "born between 1900 and 1920" range and be quite old - which might not be
    // very realistic either anyway? 🤔
    // We can assume that the person was born _before_ 1st Jan 2000, so "85" = 1985
    yearNumerical += 1900;
  } else {
    // We can assume that the person was born _after_ 31st Dec 1999, so "15" = 2015
    yearNumerical += 2000;
  }

  return yearNumerical;
};

const isValidGermanyTaxID: IdValidator = (value?: string): boolean => {
  if (!value) {
    return false;
  }
  return stdnum.DE.idnr.validate(value).isValid;
};

const isValidFrenchNIF: IdValidator = (value?: string): boolean => {
  if (!value) {
    return false;
  }
  return stdnum.FR.nif.validate(value).isValid;
};

const noValidation = () => true;

export const idValidationByCountry: Record<
  SupportedCountry,
  { validate: IdValidator; getMessage: (intl: IntlShape) => string }
> = {
  US: {
    validate: isValidUnitedStatesSSN,
    getMessage: (intl) =>
      intl.formatMessage({
        id: "Please enter a valid social security number",
        defaultMessage: "Please enter a valid social security number",
      }),
  },
  IE: {
    validate: isValidIrishPPS,
    getMessage: (intl) =>
      intl.formatMessage({
        id: "Please enter a valid PPS number",
        defaultMessage: "Please enter a valid PPS number",
      }),
  },
  GB: {
    validate: isValidBritishNIN,
    getMessage: (intl) =>
      intl.formatMessage({
        id: "Please enter a valid national insurance number",
        defaultMessage: "Please enter a valid national insurance number",
      }),
  },
  AU: {
    validate: noValidation,
    getMessage: (intl) =>
      intl.formatMessage({
        id: "Please enter a valid tax file number",
        defaultMessage: "Please enter a valid tax file number",
      }),
  },
  CA: {
    validate: noValidation,
    getMessage: (intl) =>
      intl.formatMessage({
        id: "Please enter a valid social insurance number",
        defaultMessage: "Please enter a valid social insurance number",
      }),
  },
  ES: {
    validate: isValidSpanishNIF,
    getMessage: (intl) =>
      intl.formatMessage({
        id: "Please enter a valid Número de Identificación Fiscal",
        defaultMessage: "Please enter a valid Número de Identificación Fiscal",
      }),
  },
  NL: {
    // N.B. if we need to validate the Dutch TIN (https://en.wikipedia.org/wiki/National_identification_number#Netherlands):
    // --> https://www.oecd.org/tax/automatic-exchange/crs-implementation-and-assistance/tax-identification-numbers/Netherlands-TIN.pdf
    validate: noValidation,
    getMessage: (intl) =>
      intl.formatMessage({
        id: "Please enter a valid Burgerservicenummer",
        defaultMessage: "Please enter a valid Burgerservicenummer",
      }),
  },
  BE: {
    validate: isValidBelgianNationalNumber,
    getMessage: (intl) =>
      intl.formatMessage({
        id: "Please enter a valid National Register number",
        defaultMessage: "Please enter a valid National Register number",
      }),
  },
  DK: {
    validate: isValidDanishNationalNumber,
    getMessage: (intl) =>
      intl.formatMessage({
        id: "Please enter a valid CPR Number",
        defaultMessage: "Please enter a valid CPR Number",
      }),
  },
  SE: {
    validate: isValidSwedishNationalNumber,
    getMessage: (intl) =>
      intl.formatMessage({
        id: "Please enter a valid Personnummer",
        defaultMessage: "Please enter a valid Personnummer",
      }),
  },
  NZ: {
    validate: noValidation,
    // N.B. This message is not used at the moment, since we don't ask such numbers for New Zealand,
    // and if we start using it we should check what form of SSN we should use as there are several ones there
    // --> @link https://en.wikipedia.org/wiki/National_identification_number#New_Zealand
    getMessage: (intl) =>
      intl.formatMessage({
        id: "Please enter a valid NHI number",
        defaultMessage: "Please enter a valid NHI number",
      }),
  },
  DE: {
    validate: isValidGermanyTaxID,
    getMessage: (intl) =>
      intl.formatMessage({
        id: "Please enter a valid German Steuer-Identifikationsnummer",
        defaultMessage: "Please enter a valid German Steuer-Identifikationsnummer",
      }),
  },
  FR: {
    validate: isValidFrenchNIF,
    getMessage: (intl) =>
      intl.formatMessage({
        id: "Please enter a valid French Numéro SPI",
        defaultMessage: "Please enter a valid French Numéro SPI",
      }),
  },
};
